import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { supabase } from '../lib/supabase';
import { CATEGORIES } from '../lib/constants';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import { useTheme } from '../context/ThemeContext';

const { FiBox, FiTrendingUp, FiMapPin, FiStar, FiShoppingCart, FiActivity } = FiIcons;

const Dashboard = () => {
  const { isDarkMode } = useTheme();
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({
    totalItems: 0,
    totalValue: 0,
    totalPurchase: 0,
    catStats: []
  });

  // Erweiterte Anzeige-Reihenfolge inkl. Zubehör & Ersatzteile
  const DISPLAY_ORDER = ['sets', 'loks', 'waggons', 'gleise', 'weichen', 'signale', 'zubehoer', 'zubehoer_ersatz'];

  useEffect(() => {
    const fetch = async () => {
      const { data: inv } = await supabase.from('inventory').select('*');
      if (inv) {
        const totalVal = inv.reduce((acc, curr) => acc + (curr.total_value || 0), 0);
        const totalPurchase = inv.reduce((acc, curr) => acc + ((curr.price_buy || 0) * (curr.count || 1)), 0);
        const totalItems = inv.reduce((acc, curr) => acc + (curr.count || 1), 0);
        
        const catMap = {};
        inv.forEach(i => {
          catMap[i.category] = (catMap[i.category] || 0) + (i.count || 1);
        });

        setStats({
          totalItems,
          totalValue: totalVal,
          totalPurchase: totalPurchase,
          catStats: Object.entries(catMap).map(([k, v]) => ({
            name: CATEGORIES[k]?.label || k,
            value: v,
            key: k
          }))
        });
        setData(inv);
      }
    };
    fetch();
  }, []);

  const chartOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item' },
    legend: {
      bottom: '0%',
      left: 'center',
      textStyle: { color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 9 },
      itemWidth: 10,
      itemHeight: 10
    },
    series: [{
      name: 'Bestand',
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['50%', '45%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderColor: isDarkMode ? '#1e293b' : '#fff',
        borderWidth: 2
      },
      label: { show: false },
      data: stats.catStats
    }]
  };

  const Card = ({ title, value, icon, sub, colorClass = "bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-500" }) => (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex items-center justify-between transition-colors">
      <div>
        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">{title}</p>
        <h4 className="text-xl font-black text-slate-800 dark:text-white">{value}</h4>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 uppercase font-bold">{sub}</p>
      </div>
      <div className={`${colorClass} p-3 rounded-xl`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
    </div>
  );

  const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-black text-slate-800 dark:text-white tracking-tighter">Mein Bahnbetriebswerk</h1>
        <p className="text-slate-500 dark:text-slate-400">Willkommen zurück in der Zentrale.</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card title="Gesamtbestand" value={stats.totalItems} sub="Einzelobjekte" icon={FiBox} />
        <Card title="Einkaufswert" value={formatCurrency(stats.totalPurchase)} sub="Summe Einkauf" icon={FiShoppingCart} colorClass="bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-500" />
        <Card title="Sammlungswert" value={formatCurrency(stats.totalValue)} sub="Aktueller Wert" icon={FiTrendingUp} />
        <Card title="Standorte" value={[...new Set(data.map(d => d.location))].length || 0} sub="Vitrinen & Kisten" icon={FiMapPin} colorClass="bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-500" />
        <Card title="Hersteller" value={[...new Set(data.map(d => d.manufacturer))].length || 0} sub="Markenvielfalt" icon={FiStar} colorClass="bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 min-h-[450px]">
          <h3 className="font-bold text-slate-800 dark:text-white mb-6 uppercase text-xs tracking-widest">Verteilung nach Kategorie</h3>
          <ReactECharts option={chartOption} style={{ height: '350px' }} theme={isDarkMode ? 'dark' : ''} />
        </div>

        <div className="bg-gray-800 dark:bg-slate-900 text-white p-8 rounded-2xl shadow-xl overflow-hidden relative min-h-[450px] border border-transparent dark:border-slate-800">
          <h3 className="font-bold text-yellow-500 mb-6 uppercase text-xs tracking-widest">Bestands-Übersicht</h3>
          <div className="space-y-5 relative z-10">
            {DISPLAY_ORDER.map(key => {
              const config = CATEGORIES[key];
              const stat = stats.catStats.find(s => s.key === key) || { value: 0 };
              const percentage = stats.totalItems > 0 ? (stat.value / stats.totalItems) * 100 : 0;

              return (
                <div key={key} className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold tracking-tight text-gray-100">{config?.label}</span>
                    <span className="text-sm font-black text-yellow-500">
                      {stat.value}
                      <span className="text-[10px] text-gray-400 uppercase ml-1">Stk.</span>
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-700/50 dark:bg-slate-800 rounded-full overflow-hidden border border-gray-700 dark:border-slate-700">
                    <div 
                      className={`h-full opacity-90 transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(234,179,8,0.3)] ${config?.color || 'bg-yellow-500'}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
          <SafeIcon icon={FiActivity} className="absolute -bottom-10 -right-10 text-[200px] text-white/5 pointer-events-none" />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;